import React from 'react';

import arrayMutators from 'final-form-arrays';
import { Form } from 'react-final-form';

import { Button } from '@qality/common-components/src/atoms/Button';
import { PageHeader } from '@qality/common-components/src/atoms/PageHeader';
import { ProgressTracker } from '@qality/common-components/src/atoms/ProgressTracker';

import {
  WizardFormContentStyled,
  WizardFormNavigationStyled,
  WizardFormProgressStyled,
  WizardFormWrapperStyled,
} from './WizardForm.styled';

const WizardFormView = ({
  children,
  header,
  initialValues,
  page,
  handleNext,
  handlePrev,
}: any) => {
  const steps = React.Children.toArray(children).map((wizardPage, idx) => {
    const { label, prevWording, nextWording, onCancel } = wizardPage.props;
    return {
      label: label || `${idx}`,
      prevWording: prevWording || 'Prev',
      nextWording: nextWording || 'Next',
      handleCancel: onCancel,
    };
  });

  /**
   * Trigger submit form if it was called while being on last page
   * or change to next page otherwise
   *
   * @param {FormValues} values Contain current form values
   */
  const handleSubmitForm = (values) => {
    const activePage = React.Children.toArray(children)[page];

    if (activePage && activePage.props.onSubmit) {
      const onSubmitValue = activePage.props.onSubmit(values);
      if (onSubmitValue) {
        return onSubmitValue;
      }
    }

    handleNext(values);
  };

  /**
   * Call validation on currently displayed page Component
   *
   * @param {FormValues} values Contain current form values
   */
  const validate = (values) => {
    const activePage = React.Children.toArray(children)[page];

    return activePage && activePage.props.validate
      ? activePage.props.validate(values)
      : {};
  };

  /**
   * Keep current page component
   */
  const activePageComponent = React.Children.toArray(children)[page];

  const showNavigation = activePageComponent.props.showNavigation ?? true;
  const fillPageArea = activePageComponent.props.fillPageArea ?? false;

  return (
    <Form
      initialValues={initialValues}
      validate={validate}
      onSubmit={handleSubmitForm}
      mutators={{
        // potentially other mutators could be merged here
        ...arrayMutators,
      }}
    >
      {({ handleSubmit, submitting }) => (
        <WizardFormWrapperStyled onSubmit={handleSubmit}>
          <WizardFormProgressStyled data-testid='WizardFormProgress'>
            <ProgressTracker steps={steps} currentStepNumber={page} />
          </WizardFormProgressStyled>
          {header && <PageHeader>{header}</PageHeader>}
          <WizardFormContentStyled
            fillPageArea={fillPageArea}
            data-testid='WizardFormContent'
          >
            {activePageComponent}
          </WizardFormContentStyled>
          {showNavigation && (
            <WizardFormNavigationStyled data-testid='WizardFormNavigation'>
              <Button
                mr
                appearance='default'
                isDisabled={page === 0}
                type='button'
                onClick={handlePrev}
              >
                {steps[page]?.prevWording}
              </Button>

              <Button appearance='primary' disabled={submitting} type='submit'>
                {steps[page]?.nextWording}
              </Button>

              {steps[page]?.handleCancel && (
                <Button
                  ml
                  appearance='link'
                  onClick={steps[page]?.handleCancel}
                  disabled={submitting}
                  type='button'
                >
                  Cancel
                </Button>
              )}
            </WizardFormNavigationStyled>
          )}
        </WizardFormWrapperStyled>
      )}
    </Form>
  );
};

export default WizardFormView;
